import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, CheckCircle2, Clock, AlertCircle, Upload, PenLine } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { api } from '../../lib/api';
import { statusLabel, statusTone, fmtDateTime, TAX_FILE_LABELS, SIGNATURE_LABELS } from '../../lib/docs';
import MemberTaxFilingProgressCard from '../../components/MemberTaxFilingProgressCard';
import { toast } from 'sonner';

export default function MemberTaxFilePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setData(await api.myTaxFile()); }
    catch { toast.error('Could not load your tax file'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) return (<><Header /><div className="py-20 text-center text-neutral-500">Loading…</div><Footer /></>);

  const tf = data.tax_file;
  const required = data.categories.filter((c) => c.required);
  const haveByCat = new Set(data.documents.map((d) => d.category_id));

  return (
    <>
      <Header />
      <section className="py-10 bg-[#0c0a08] border-b border-neutral-800 text-white">
        <div className="max-w-[1240px] mx-auto px-6">
          <Link to="/account" className="text-[12px] uppercase tracking-wider text-[#f5b300] inline-flex items-center gap-1 hover:underline mb-2">
            <ArrowLeft className="w-3.5 h-3.5" /> Account
          </Link>
          <h1 className="text-[28px] md:text-[32px] font-extrabold text-[#f5b300]">Tax file · {tf.tax_year}</h1>
          <p className="text-[13.5px] text-neutral-400 mt-1">Track everything in one place — what we have, what we need, and where we are in the process.</p>
        </div>
      </section>

      <section className="py-10 bg-white">
        <div className="max-w-[1240px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-7">
          <div className="lg:col-span-2 space-y-6">
            <MemberTaxFilingProgressCard />

            <div className="rounded-xl border border-neutral-100 bg-white p-6">
              <p className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">Current status</p>
              <p className="text-[26px] font-extrabold text-[#14110f]">{TAX_FILE_LABELS[tf.status] || tf.status}</p>
              {tf.notes && <p className="mt-3 text-[13px] text-neutral-700 bg-[#fbf7ee] p-3 rounded-md">{tf.notes}</p>}
              <p className="mt-4 text-[12.5px] text-neutral-500">Last updated {fmtDateTime(tf.updated_at)}</p>
            </div>

            <div className="rounded-xl border border-neutral-100 bg-white p-6">
              <h3 className="text-[16px] font-bold text-[#14110f] mb-4">Required documents checklist</h3>
              {required.length === 0 ? <p className="text-[13px] text-neutral-500">No required documents configured.</p>
                : <ul className="divide-y divide-neutral-100" data-testid="required-checklist">
                    {required.map((c) => {
                      const have = haveByCat.has(c.id);
                      return (
                        <li key={c.id} className="py-3 flex items-center gap-3">
                          {have ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                            : <AlertCircle className="w-5 h-5 text-amber-500 shrink-0" />}
                          <div className="flex-1">
                            <p className="text-[14px] font-semibold text-[#14110f]">{c.name}</p>
                            <p className="text-[12.5px] text-neutral-500">{c.description}</p>
                          </div>
                          <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${have ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                            {have ? 'On file' : 'Outstanding'}
                          </span>
                        </li>
                      );
                    })}
                  </ul>}
            </div>

            {data.open_document_requests.length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-6">
                <h3 className="text-[16px] font-bold text-[#14110f] mb-4 flex items-center gap-2"><AlertCircle className="w-4 h-4 text-amber-600" /> Items your tax specialist asked for</h3>
                <ul className="space-y-3" data-testid="open-requests">
                  {data.open_document_requests.map((r) => (
                    <li key={r.id} className="rounded-lg bg-white border border-amber-200 p-3.5">
                      <p className="text-[13.5px] font-semibold text-[#14110f]">{r.category?.name || 'Document'}</p>
                      {r.message && <p className="text-[12.5px] text-neutral-700 mt-0.5">{r.message}</p>}
                      <Link to="/account/documents/upload" className="text-[12px] font-semibold text-[#c89200] hover:underline mt-1.5 inline-flex items-center gap-1">
                        <Upload className="w-3.5 h-3.5" /> Upload this
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data.signature_requests.length > 0 && (
              <div className="rounded-xl border border-neutral-100 bg-white p-6">
                <h3 className="text-[16px] font-bold text-[#14110f] mb-4 flex items-center gap-2"><PenLine className="w-4 h-4 text-[#c89200]" /> Signature requests</h3>
                <ul className="space-y-3" data-testid="signature-requests">
                  {data.signature_requests.map((s) => (
                    <SignatureItem key={s.id} sig={s} onReload={load} />
                  ))}
                </ul>
              </div>
            )}
          </div>

          <aside className="space-y-5">
            <div className="rounded-xl border border-neutral-100 bg-white p-5">
              <h3 className="text-[14px] font-bold text-[#14110f] mb-3">Your tax specialist</h3>
              {data.specialist ? (
                <div>
                  <p className="text-[14px] font-semibold text-[#14110f]">{data.specialist.name}</p>
                  <p className="text-[12.5px] text-neutral-500">{data.specialist.email}</p>
                </div>
              ) : <p className="text-[12.5px] text-neutral-500">A WYSE Tribe specialist will be assigned soon.</p>}
            </div>
            <div className="rounded-xl border border-neutral-100 bg-white p-5">
              <h3 className="text-[14px] font-bold text-[#14110f] mb-3">Documents on file</h3>
              <p className="text-[32px] font-extrabold text-[#f5b300] leading-none">{data.documents.length}</p>
              <Link to="/account/documents" className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-[#c89200] hover:underline">
                Open vault <ArrowLeft className="w-3 h-3 rotate-180" />
              </Link>
            </div>
            <div className="rounded-xl border border-neutral-100 bg-[#fbf7ee] p-5">
              <h3 className="text-[14px] font-bold text-[#14110f] mb-1">Need to add something?</h3>
              <p className="text-[12.5px] text-neutral-700">Use the secure upload — your specialist sees it the moment it lands.</p>
              <Link to="/account/documents/upload" className="mt-3 inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold px-4 py-2 rounded-md text-[12.5px]">
                <Upload className="w-3.5 h-3.5" /> Upload
              </Link>
            </div>
          </aside>
        </div>
      </section>
      <Footer />
    </>
  );
}

function SignatureItem({ sig, onReload }) {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      setBusy(true);
      await api.mySignatureUploadSigned(sig.id, fd);
      toast.success('Signed copy received');
      onReload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); }
  };
  return (
    <li className="rounded-lg border border-neutral-100 p-3.5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[13.5px] font-semibold text-[#14110f]">{sig.title}</p>
          {sig.message && <p className="text-[12.5px] text-neutral-600 mt-0.5">{sig.message}</p>}
          <p className="text-[11px] text-neutral-500 mt-1">Requested {fmtDateTime(sig.requested_at)}</p>
        </div>
        <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-fuchsia-50 text-fuchsia-700">{SIGNATURE_LABELS[sig.status] || sig.status}</span>
      </div>
      {sig.status !== 'signed' && (
        <div className="mt-3">
          <button onClick={() => fileRef.current?.click()} disabled={busy}
                  className="inline-flex items-center gap-2 bg-[#14110f] hover:bg-[#291d12] text-white font-semibold px-3.5 py-2 rounded-md text-[12.5px] disabled:opacity-50">
            <Upload className="w-3.5 h-3.5" /> Upload signed copy
          </button>
          <input ref={fileRef} type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        </div>
      )}
    </li>
  );
}
